import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  AppBar,
  Box,
  Button,
  Chip,
  Collapse,
  CssBaseline,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Portal,
  Snackbar,
  Stack,
  Switch,
  Tab,
  Tabs,
  TextField,
  ThemeProvider,
  Toolbar,
  Typography,
  createTheme,
  useMediaQuery,
} from '@mui/material';
import AdminDashboard from './pages/AdminDashboard';
import GenerateSchema from './pages/GenerateSchema';
import LocationRequests from './pages/LocationRequests';
import { LocationReviewActionsProvider } from './components/LocationReviewActions';
import PendingRequestsOverview from './components/PendingRequestsOverview';
import CancelPendingRequestDialog from './components/CancelPendingRequestDialog';
import { authenticateAccessPassword, decodeJwtPayload } from './helpers/accessApi';
import { compareDateValues, formatDateTimesInText } from './helpers/dateTime';
import { cancelLocationRequest, fetchLocationRequests } from './helpers/userRequestsApi';
import { requestUserAccess } from './helpers/userPasswordsApi';
import { sendPendingRequestsNotification } from './helpers/notificationsApi';
import { clearSheetCache } from './helpers/sheetCache';

const SESSION_STORAGE_KEY = 'church_user_manager_session_token';
const ACCESS_PASSWORD_QUERY_PARAM = 'accessPassword';
const COLOR_MODE_STORAGE_KEY = 'church_user_manager_color_mode';
const BRAND_ASSET_URL = `${process.env.PUBLIC_URL || ''}/brand`;
const BRAND_LOGO_ALT = 'Departamento Internacional de Idiomas — Iglesia de Dios Ministerial de Jesucristo Internacional';

function BrandLogo() {
  return (
    <Box
      role="img"
      aria-label={BRAND_LOGO_ALT}
      sx={{
        display: 'grid',
        gridTemplateColumns: 'minmax(0, 1fr) 1px minmax(0, 1.65fr)',
        alignItems: 'center',
        gap: { xs: 1, sm: 1.5 },
        width: '100%',
        maxWidth: 380,
        minHeight: { xs: 64, sm: 76 },
        px: { xs: 1.25, sm: 2 },
        py: 0.5,
        borderRadius: 2,
        bgcolor: '#1f2e85',
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.12)',
        isolation: 'isolate',
        '& img': {
          display: 'block',
          width: '100%',
          height: 'auto',
          mixBlendMode: 'lighten',
        },
      }}
    >
      <Box component="img" src={`${BRAND_ASSET_URL}/half_1.png`} alt="" width={1000} height={568} />
      <Box aria-hidden="true" sx={{ width: 1, height: { xs: 40, sm: 52 }, bgcolor: '#e2c16d' }} />
      <Box component="img" src={`${BRAND_ASSET_URL}/half_2.png`} alt="" width={1888} height={455} />
    </Box>
  );
}

function consumeAccessPasswordFromUrl() {
  if (typeof window === 'undefined') {
    return '';
  }

  try {
    const currentUrl = new URL(window.location.href);
    const accessPassword = String(
      currentUrl.searchParams.get(ACCESS_PASSWORD_QUERY_PARAM) || ''
    ).trim();

    if (!accessPassword) {
      return '';
    }

    currentUrl.searchParams.delete(ACCESS_PASSWORD_QUERY_PARAM);
    const cleanedUrl = `${currentUrl.pathname}${currentUrl.search}${currentUrl.hash}`;
    window.history.replaceState({}, document.title, cleanedUrl);
    return accessPassword;
  } catch {
    return '';
  }
}

function clearStoredSessionToken() {
  clearSheetCache();
  try {
    window.localStorage.removeItem(SESSION_STORAGE_KEY);
  } catch {
    // ignore storage errors
  }
}

function loadStoredSession() {
  try {
    const storedToken = window.localStorage.getItem(SESSION_STORAGE_KEY);
    if (!storedToken) {
      return null;
    }

    const claims = decodeJwtPayload(storedToken);
    if (!claims || !claims.level || !claims.role) {
      clearStoredSessionToken();
      return null;
    }

    const expiresAtMs = Number(claims.exp || 0) * 1000;
    if (!expiresAtMs || Number.isNaN(expiresAtMs) || expiresAtMs <= Date.now()) {
      clearStoredSessionToken();
      return null;
    }

    return {
      token: storedToken,
      claims,
    };
  } catch {
    return null;
  }
}

function persistSessionToken(token) {
  try {
    window.localStorage.setItem(SESSION_STORAGE_KEY, token);
  } catch {
    // ignore storage errors
  }
}

function loadStoredColorMode() {
  if (typeof window === 'undefined') {
    return 'light';
  }

  try {
    const storedMode = String(window.localStorage.getItem(COLOR_MODE_STORAGE_KEY) || '').trim().toLowerCase();
    if (storedMode === 'light' || storedMode === 'dark') {
      return storedMode;
    }

    return 'light';
  } catch {
    return 'light';
  }
}

function persistColorMode(mode) {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    window.localStorage.setItem(COLOR_MODE_STORAGE_KEY, mode === 'dark' ? 'dark' : 'light');
  } catch {
    // ignore storage errors
  }
}

const ACCESS_LEVELS = {
  MAIN_ADMIN: 1,
  MANAGER: 2,
  REGULAR_USER: 3,
};

function sortLocationRequests(requests) {
  return [...requests].sort((left, right) => compareDateValues(right?.fecha, left?.fecha));
}

function getNotificationErrorMessage(error) {
  const normalized = String(error?.message || '').trim();
  if (/No hay (?:correos|destinatarios)|No se encontraron destinatarios/i.test(normalized)) {
    return 'No hay destinatarios configurados. Contacta a tu administrador.';
  }
  return normalized.split('\n')[0]
    .replace(/^Unexpected response type:[^-]*-\s*/i, '')
    .replace(/^HttpError:\s*/i, '')
    .trim() || 'No se pudo enviar el aviso. Inténtalo de nuevo.';
}

const PAGE_ACCESS = {
  "Todas las hojas para administradores": {
    Component: AdminDashboard,
    allowedLevels: [ACCESS_LEVELS.MAIN_ADMIN, ACCESS_LEVELS.REGULAR_USER],
  },
  "Generar schema": {
    Component: GenerateSchema,
    allowedLevels: [ACCESS_LEVELS.MAIN_ADMIN],
  },
  "Solicitudes de mi sede": {
    Component: LocationRequests,
    allowedLevels: [ACCESS_LEVELS.REGULAR_USER],
  },
};

function getPageLabel(pageKey, accessLevel) {
  if (pageKey === 'Todas las hojas para administradores' && accessLevel === ACCESS_LEVELS.REGULAR_USER) {
    return 'Usuarios de mi sede';
  }

  return pageKey;
}

function createAppTheme(mode) {
  const isDark = mode === 'dark';

  return createTheme({
    palette: {
      mode,
      primary: {
        main: isDark ? '#7ea2ff' : '#2f66f3',
      },
      background: {
        default: isDark ? '#111827' : '#f7f9fc',
        paper: isDark ? '#18212f' : '#ffffff',
      },
      text: {
        primary: isDark ? '#e5eefc' : '#1f2a44',
        secondary: isDark ? '#aebdd6' : undefined,
      },
      divider: isDark ? '#2b3a52' : '#e3e8f2',
    },
    typography: {
      fontFamily:
        "'Segoe UI', -apple-system, BlinkMacSystemFont, 'Roboto', 'Helvetica Neue', Arial, sans-serif",
      fontWeightLight: 200,
      fontWeightRegular: 300,
      fontWeightMedium: 400,
      fontWeightBold: 500,
      h6: {
        letterSpacing: 0.2,
      },
    },
    components: {
      MuiIconButton: {
        defaultProps: {
          size: 'small',
        },
        styleOverrides: {
          root: {
            padding: 4,
          },
        },
      },
      MuiTextField: {
        defaultProps: {
          size: 'small',
        },
      },
      MuiInputBase: {
        styleOverrides: {
          input: {
            paddingTop: 7,
            paddingBottom: 7,
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            boxShadow: 'none',
            backgroundImage: 'none',
          },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          root: {
            borderColor: isDark ? '#2f415c' : '#e7ecf5',
            paddingTop: 8,
            paddingBottom: 8,
          },
        },
      },
      MuiFormControl: {
        defaultProps: {
          size: 'small',
        },
      },
    },
  });
}

function App() {
  const initialAccessPasswordRef = useRef(null);

  if (initialAccessPasswordRef.current === null) {
    initialAccessPasswordRef.current = consumeAccessPasswordFromUrl();
  }

  const [passwordInput, setPasswordInput] = useState(() => initialAccessPasswordRef.current || '');
  const [authError, setAuthError] = useState('');
  const [authLoading, setAuthLoading] = useState(false);
  const [accessRequestEmail, setAccessRequestEmail] = useState('');
  const [isAccessRequestOpen, setIsAccessRequestOpen] = useState(false);
  const [accessRequestLoading, setAccessRequestLoading] = useState(false);
  const [accessRequestFeedback, setAccessRequestFeedback] = useState({ type: '', message: '' });
  const [session, setSession] = useState(() => loadStoredSession());
  const [colorMode, setColorMode] = useState(() => loadStoredColorMode());
  const [shouldAutoLogin, setShouldAutoLogin] = useState(
    () => Boolean(initialAccessPasswordRef.current)
  );
  const [activePageKey, setActivePageKey] = useState('');
  const [locationRequestState, setLocationRequestState] = useState({
    requests: [],
    loading: false,
    error: '',
    hasLoaded: false,
  });
  const pendingNotificationRef = useRef(null);
  const [notificationState, setNotificationState] = useState(null);
  const [pendingOverviewOpen, setPendingOverviewOpen] = useState(false);
  const [cancelSelection, setCancelSelection] = useState(null);
  const [cancellingRequests, setCancellingRequests] = useState(false);
  const [cancellationError, setCancellationError] = useState('');
  const [cancellationFeedback, setCancellationFeedback] = useState(null);
  const pendingCancellationRef = useRef(null);
  const sessionTokenRef = useRef(session?.token);
  const locationRequestLoadRef = useRef(0);
  sessionTokenRef.current = session?.token;
  const theme = useMemo(() => createAppTheme(colorMode), [colorMode]);
  const isMobileViewport = useMediaQuery(theme.breakpoints.down('sm'));
  const [isToolbarOptionsOpen, setIsToolbarOptionsOpen] = useState(false);

  const isRegularUser = session?.claims?.level === ACCESS_LEVELS.REGULAR_USER;

  useEffect(() => {
    pendingNotificationRef.current = null;
    setNotificationState(null);
    setPendingOverviewOpen(false);
    pendingCancellationRef.current = null;
    setCancelSelection(null);
    setCancellingRequests(false);
    setCancellationError('');
    setCancellationFeedback(null);
    locationRequestLoadRef.current += 1;
    setLocationRequestState({ requests: [], loading: false, error: '', hasLoaded: false });
  }, [session?.token]);

  async function handleSendPendingRequestsEmail() {
    if (pendingNotificationRef.current || !isRegularUser || !locationRequestState.requests.length) return;

    const operation = { token: session.token, requests: locationRequestState.requests };
    pendingNotificationRef.current = operation;
    setNotificationState({ ...operation, sending: true, type: '', message: '' });

    try {
      await sendPendingRequestsNotification(operation.token);
      if (pendingNotificationRef.current !== operation) return;
      setNotificationState({ ...operation, sending: false, type: 'success', message: 'Administradores y responsables avisados.' });
    } catch (error) {
      if (pendingNotificationRef.current !== operation) return;
      setNotificationState({ ...operation, sending: false, type: 'error', message: getNotificationErrorMessage(error) });
    } finally {
      if (pendingNotificationRef.current === operation) pendingNotificationRef.current = null;
    }
  }

  const notificationSending = notificationState?.token === session?.token && Boolean(notificationState?.sending);
  const notificationFeedback = notificationState?.token === session?.token
    && notificationState?.requests === locationRequestState.requests
    ? notificationState
    : null;

  useEffect(() => {
    persistColorMode(colorMode);
  }, [colorMode]);

  useEffect(() => {
    const expiresAtMs = Number(session?.claims?.exp || 0) * 1000;

    if (!expiresAtMs || Number.isNaN(expiresAtMs)) {
      return undefined;
    }

    const remainingMs = expiresAtMs - Date.now();

    if (remainingMs <= 0) {
      clearStoredSessionToken();
      setSession(null);
      setAuthError('Tu sesión expiró. Ingresa nuevamente.');
      return undefined;
    }

    const timeoutId = window.setTimeout(() => {
      clearStoredSessionToken();
      setSession(null);
      setAuthError('Tu sesión expiró. Ingresa nuevamente.');
    }, remainingMs);

    return () => window.clearTimeout(timeoutId);
  }, [session?.claims?.exp]);

  const loadLocationRequests = useCallback(async ({ force = false } = {}) => {
    if (!isRegularUser || !session?.token) {
      return;
    }

    if (!force && (locationRequestState.loading || locationRequestState.hasLoaded)) {
      return;
    }

    const token = session.token;
    const loadId = ++locationRequestLoadRef.current;

    setLocationRequestState((previous) => ({
      ...previous,
      loading: true,
      error: '',
    }));

    try {
      const result = await fetchLocationRequests(token);
      if (sessionTokenRef.current !== token || locationRequestLoadRef.current !== loadId) return;
      setLocationRequestState({
        requests: sortLocationRequests(result?.requests || []),
        loading: false,
        error: '',
        hasLoaded: true,
      });
      return true;
    } catch (requestError) {
      if (sessionTokenRef.current !== token || locationRequestLoadRef.current !== loadId) return;
      setLocationRequestState((previous) => ({
        ...previous,
        loading: false,
        error: requestError.message || 'No se pudieron cargar las solicitudes de tu sede.',
        hasLoaded: true,
      }));
      return false;
    }
  }, [isRegularUser, locationRequestState.hasLoaded, locationRequestState.loading, session?.token]);

  useEffect(() => {
    if (!isRegularUser || !session?.token) {
      setLocationRequestState({
        requests: [],
        loading: false,
        error: '',
        hasLoaded: false,
      });
      return;
    }

    loadLocationRequests();
  }, [isRegularUser, loadLocationRequests, session?.token]);

  const upsertLocationRequest = useCallback((request) => {
    if (!request?.sheetName || !request?.sheetRowNumber) {
      return;
    }

    setLocationRequestState((previous) => {
      const existingIndex = previous.requests.findIndex(
        (currentRequest) =>
          currentRequest.sheetName === request.sheetName
          && Number(currentRequest.sheetRowNumber) === Number(request.sheetRowNumber)
      );

      const nextRequests = [...previous.requests];

      if (existingIndex >= 0) {
        nextRequests[existingIndex] = request;
      } else {
        nextRequests.push(request);
      }

      return {
        ...previous,
        requests: sortLocationRequests(nextRequests),
        error: '',
        hasLoaded: true,
      };
    });
  }, []);

  const removeLocationRequest = useCallback(({ sheetName, sheetRowNumber }) => {
    if (!sheetName || !sheetRowNumber) {
      return;
    }

    const targetRowNumber = Number(sheetRowNumber);

    setLocationRequestState((previous) => ({
      ...previous,
      requests: previous.requests
        .filter(
          (request) =>
            !(
              request.sheetName === sheetName
              && Number(request.sheetRowNumber) === targetRowNumber
            )
        )
        .map((request) => {
          if (request.sheetName !== sheetName || Number(request.sheetRowNumber) < targetRowNumber) {
            return request;
          }

          return {
            ...request,
            sheetRowNumber: Number(request.sheetRowNumber) - 1,
          };
        }),
      error: '',
      hasLoaded: true,
    }));
  }, []);

  const openCancelRequest = useCallback((request) => {
    if (!isRegularUser || pendingCancellationRef.current || !request?.sheetName || !request?.sheetRowNumber) return;
    setCancelSelection(request);
    setCancellationError('');
    setCancellationFeedback(null);
  }, [isRegularUser]);

  const closeCancelRequest = useCallback(() => {
    if (pendingCancellationRef.current) return;
    setCancelSelection(null);
    setCancellationError('');
  }, []);

  async function confirmCancelRequest() {
    if (pendingCancellationRef.current || !isRegularUser || !cancelSelection || !session?.token) return;

    const operation = { token: session.token, request: cancelSelection };
    pendingCancellationRef.current = operation;
    setCancellingRequests(true);
    setCancellationError('');
    const isCurrent = () => pendingCancellationRef.current === operation && sessionTokenRef.current === operation.token;

    try {
      const currentRequest = locationRequestState.requests.find((request) => (
        request.sheetName === operation.request.sheetName
        && Number(request.sheetRowNumber) === Number(operation.request.sheetRowNumber)
        && (!operation.request.requestId || request.requestId === operation.request.requestId)
      ));
      if (!currentRequest || locationRequestState.loading) {
        setCancelSelection(null);
        setCancellationFeedback({ type: 'error', message: 'La lista cambió. Revisa las solicitudes y vuelve a seleccionar los cambios que deseas cancelar.' });
        const refreshed = await loadLocationRequests({ force: true });
        if (isCurrent() && !refreshed) setCancellationFeedback({ type: 'error', message: 'No se pudo actualizar la lista. Actualiza las solicitudes antes de volver a cancelar.' });
        return;
      }

      await cancelLocationRequest({
        sheetName: operation.request.sheetName,
        sheetRowNumber: operation.request.sheetRowNumber,
        ...(operation.request.requestId ? { expectedRequestId: operation.request.requestId } : {}),
      }, operation.token);
      if (!isCurrent()) return;
      removeLocationRequest(operation.request);
      setCancelSelection(null);
      setCancellationFeedback({ type: 'success', message: 'Cambios cancelados. Se mantienen los datos actuales.' });
    } catch (error) {
      if (!isCurrent()) return;
      if ([404, 409].includes(Number(error?.status))) {
        setCancelSelection(null);
        setCancellationFeedback({ type: 'error', message: 'La solicitud cambió o ya no está pendiente. Revisa la lista actualizada.' });
        const refreshed = await loadLocationRequests({ force: true });
        if (isCurrent() && !refreshed) setCancellationFeedback({ type: 'error', message: 'No se pudo actualizar la lista. Actualiza las solicitudes antes de volver a cancelar.' });
      } else {
        setCancellationError(error?.message || 'No se pudieron cancelar los cambios. Inténtalo de nuevo.');
      }
    } finally {
      if (isCurrent()) {
        pendingCancellationRef.current = null;
        setCancellingRequests(false);
      }
    }
  }

  const closeCancellationFeedback = useCallback((_, reason) => {
    if (reason !== 'clickaway') setCancellationFeedback(null);
  }, []);

  const locationPendingRequestCount = locationRequestState.requests.length;

  const visiblePages = useMemo(() => {
    if (!session?.claims?.level) {
      return [];
    }

    return Object.entries(PAGE_ACCESS)
      .filter(([, config]) => config.allowedLevels.includes(session.claims.level))
      .map(([key, config]) => ({
        key,
        label: getPageLabel(key, session.claims.level),
        Component: config.Component,
      }));
  }, [session]);

  useEffect(() => {
    if (visiblePages.length === 0) {
      setActivePageKey('');
      return;
    }

    if (!visiblePages.some((page) => page.key === activePageKey)) {
      setActivePageKey(visiblePages[0].key);
    }
  }, [activePageKey, visiblePages]);

  const activePage = useMemo(
    () => visiblePages.find((page) => page.key === activePageKey) || null,
    [activePageKey, visiblePages]
  );

  const authenticateWithPassword = useCallback(async (submittedPassword) => {
    const normalizedPassword = String(submittedPassword || '').trim();

    if (!normalizedPassword) {
      setAuthError('La contraseña es obligatoria.');
      return false;
    }

    setAuthLoading(true);
    setAuthError('');

    try {
      const authData = await authenticateAccessPassword(normalizedPassword);
      const claims = decodeJwtPayload(authData.token);

      if (!claims || !claims.level || !claims.role) {
        throw new Error('Token inválido.');
      }

      setSession({
        token: authData.token,
        claims,
      });
      persistSessionToken(authData.token);
      setPasswordInput('');
      return true;
    } catch (error) {
      setSession(null);
      clearStoredSessionToken();
      setAuthError(error.message || 'La autenticación falló.');
      return false;
    } finally {
      setAuthLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!shouldAutoLogin || session || authLoading) {
      return;
    }

    setShouldAutoLogin(false);
    authenticateWithPassword(passwordInput);
  }, [authLoading, authenticateWithPassword, passwordInput, session, shouldAutoLogin]);

  async function handleLogin(event) {
    event.preventDefault();
    await authenticateWithPassword(passwordInput);
  }

  async function handleRequestAccess(event) {
    event.preventDefault();
    const email = String(accessRequestEmail || '').trim();
    if (!email) {
      setAccessRequestFeedback({ type: 'error', message: 'Ingresa tu correo electrónico.' });
      return;
    }

    setAccessRequestLoading(true);
    setAccessRequestFeedback({ type: '', message: '' });
    try {
      const result = await requestUserAccess(email, window.location.origin);
      setAccessRequestFeedback({
        type: 'success',
        message: result?.message || 'Si el correo está registrado, recibirás un enlace de acceso válido durante 24 horas.',
      });
      setAccessRequestEmail('');
    } catch (error) {
      setAccessRequestFeedback({
        type: 'error',
        message: error.message || 'No se pudo procesar la solicitud de acceso.',
      });
    } finally {
      setAccessRequestLoading(false);
    }
  }

  function handleLogout() {
    setSession(null);
    clearStoredSessionToken();
    setPasswordInput('');
    setAuthError('');
  }

  function handleToggleColorMode() {
    setColorMode((previousMode) => (previousMode === 'dark' ? 'light' : 'dark'));
  }

  useEffect(() => {
    if (!isMobileViewport) {
      setIsToolbarOptionsOpen(false);
    }
  }, [isMobileViewport]);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Box
        sx={{
          p: { xs: 0.5, md: 0.5 },
          minHeight: '100vh',
          ...(!session && {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }),
        }}
      >
        {!session && (
          <Paper variant="outlined" sx={{ p: 2, width: '100%', maxWidth: 640 }}>
            <Stack spacing={1.25}>
              <Stack alignItems="center" spacing={1} sx={{ pb: 1 }}>
                <BrandLogo />
                <Typography component="h1" variant="h6" align="center">
                  Departamento Internacional de Idiomas
                </Typography>
                <Typography variant="body2" color="text.secondary" align="center">
                  Verificación de usuarios
                </Typography>
              </Stack>
              <Typography variant="h6">Acceso</Typography>
              <Stack component="form" spacing={1.25} onSubmit={handleLogin}>
                <TextField
                  label="Contraseña"
                  type="password"
                  value={passwordInput}
                  onChange={(event) => setPasswordInput(event.target.value)}
                  disabled={authLoading}
                />
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                  <Button type="submit" variant="contained" disabled={authLoading || accessRequestLoading}>
                    Ingresar
                  </Button>
                  <Button
                    type="button"
                    variant="text"
                    onClick={() => {
                      setIsAccessRequestOpen((previous) => !previous);
                      setAccessRequestFeedback({ type: '', message: '' });
                    }}
                    disabled={authLoading || accessRequestLoading}
                  >
                    Solicitar acceso
                  </Button>
                </Stack>
              </Stack>
              {authError && <Alert severity="error">{formatDateTimesInText(authError)}</Alert>}

              <Collapse in={isAccessRequestOpen} timeout="auto" unmountOnExit>
                <Stack
                  component="form"
                  spacing={1.25}
                  onSubmit={handleRequestAccess}
                  sx={{ mt: 0.5, pt: 1.5, borderTop: 1, borderColor: 'divider' }}
                >
                  <Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>Solicitar acceso por correo</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Si tu correo está registrado, recibirás un enlace de acceso temporal válido durante 24 horas.
                    </Typography>
                  </Box>
                  <TextField
                    label="Correo electrónico"
                    type="email"
                    autoComplete="email"
                    value={accessRequestEmail}
                    onChange={(event) => setAccessRequestEmail(event.target.value)}
                    disabled={accessRequestLoading}
                    required
                  />
                  <Box>
                    <Button type="submit" variant="contained" disabled={accessRequestLoading}>
                      {accessRequestLoading ? 'Enviando...' : 'Enviar enlace de acceso'}
                    </Button>
                  </Box>
                  {accessRequestFeedback.message && (
                    <Alert severity={accessRequestFeedback.type === 'success' ? 'success' : 'error'}>
                      {accessRequestFeedback.message}
                    </Alert>
                  )}
                </Stack>
              </Collapse>
            </Stack>
          </Paper>
        )}

        {session && (
          <Stack>
            <AppBar
              position="static"
              elevation={0}
              sx={{
                border: 1,
                borderColor: colorMode === 'dark' ? '#324d7a' : '#173b7d',
                borderRadius: 1,
                bgcolor: '#173b7d',
                backgroundImage: 'linear-gradient(110deg, #183f83 0%, #133576 100%)',
                color: '#ffffff',
              }}
            >
              <Toolbar
                disableGutters
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: 'minmax(0, 1fr)', lg: 'minmax(0, 1fr) auto minmax(0, 1fr)' },
                  px: { xs: 1, md: 1.5 },
                  py: 1,
                  gap: { xs: 1, md: 1.5 },
                }}
              >
                <Tabs
                  value={activePageKey || false}
                  onChange={(_, nextPageKey) => setActivePageKey(nextPageKey)}
                  orientation={isMobileViewport ? 'vertical' : 'horizontal'}
                  variant={isMobileViewport ? 'standard' : 'scrollable'}
                  scrollButtons={isMobileViewport ? false : 'auto'}
                  allowScrollButtonsMobile={!isMobileViewport}
                  aria-label="Navegación principal"
                  sx={{
                    gridColumn: 1,
                    gridRow: { xs: 2, lg: 1 },
                    width: '100%',
                    minWidth: 0,
                    minHeight: 0,
                    '& .MuiTabs-flexContainer': {
                      gap: 0.5,
                    },
                    '& .MuiTabs-indicator': {
                      height: isMobileViewport ? '100%' : 3,
                      width: isMobileViewport ? 3 : '100%',
                      borderRadius: 999,
                      backgroundColor: '#e2c16d',
                    },
                  }}
                >
                  {visiblePages.map((page) => (
                    <Tab
                      key={page.key}
                      value={page.key}
                      disableRipple
                      label={(
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <span>{page.label}</span>
                          {isRegularUser && page.key === 'Solicitudes de mi sede' && (
                            <Chip
                              label={locationPendingRequestCount}
                              color="error"
                              size="small"
                            />
                          )}
                        </Box>
                      )}
                      sx={{
                        minHeight: 0,
                        py: 1.25,
                        px: 1,
                        textTransform: 'none',
                        alignItems: isMobileViewport ? 'flex-start' : 'center',
                        justifyContent: isMobileViewport ? 'flex-start' : 'center',
                        textAlign: isMobileViewport ? 'left' : 'center',
                        width: isMobileViewport ? '100%' : 'auto',
                        borderBottom: '3px solid transparent',
                        color: 'rgba(255, 255, 255, 0.85)',
                        fontSize: '0.85rem',
                        fontWeight: 500,
                        '&.Mui-selected': {
                          color: '#ffffff',
                        },
                        '&.Mui-focusVisible': {
                          outline: '2px solid #e2c16d',
                          outlineOffset: -2,
                        },
                      }}
                    />
                  ))}
                </Tabs>
                <Box
                  sx={{
                    gridColumn: { xs: 1, lg: 2 },
                    gridRow: 1,
                    justifySelf: 'center',
                    width: { xs: '100%', lg: 380 },
                    maxWidth: 380,
                  }}
                >
                  <BrandLogo />
                </Box>
                <Box
                  sx={{
                    gridColumn: { xs: 1, lg: 3 },
                    gridRow: { xs: 3, lg: 1 },
                    minWidth: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: { xs: 'center', lg: 'flex-end' },
                    gap: 1.5,
                    '& .MuiButton-root': { color: '#ffffff', borderColor: 'rgba(255, 255, 255, 0.45)' },
                    '& .MuiButton-root:hover': { bgcolor: 'rgba(255, 255, 255, 0.1)' },
                    '& .MuiChip-root': {
                      color: '#ffffff',
                      borderColor: 'rgba(255, 255, 255, 0.4)',
                      bgcolor: 'rgba(255, 255, 255, 0.12)',
                    },
                    '& .MuiSwitch-switchBase': { color: '#ffffff' },
                    '& .MuiSwitch-switchBase.Mui-checked': { color: '#e2c16d' },
                    '& .MuiSwitch-track': { bgcolor: '#ffffff' },
                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: '#e2c16d' },
                  }}
                >
                  {isMobileViewport ? (
                    <Box sx={{ width: '100%' }}>
                      <Button
                        type="button"
                        variant={isToolbarOptionsOpen ? 'outlined' : 'text'}
                        onClick={() => setIsToolbarOptionsOpen((previous) => !previous)}
                        sx={{ width: '100%', textTransform: 'none' }}
                      >
                        {isToolbarOptionsOpen ? 'Ocultar opciones' : 'Mostrar opciones'}
                      </Button>
                      <Collapse in={isToolbarOptionsOpen} timeout="auto" unmountOnExit>
                        <Stack spacing={1} sx={{ pt: 1 }}>
                          <Chip
                            label={`${session.claims?.email || 'Super Admin'}`}
                            color="primary"
                            variant="outlined"
                            sx={{
                              width: '100%',
                              maxWidth: '100%',
                              '& .MuiChip-label': {
                                display: 'block',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                              },
                            }}
                          />
                          <Stack
                            direction="row"
                            spacing={0.75}
                            alignItems="center"
                            sx={{ width: '100%', justifyContent: 'space-between' }}
                          >
                            <Typography variant="body2" sx={{ whiteSpace: 'nowrap' }}>
                              Modo oscuro
                            </Typography>
                            <Switch
                              checked={colorMode === 'dark'}
                              onChange={handleToggleColorMode}
                              inputProps={{ 'aria-label': 'Alternar modo oscuro' }}
                            />
                          </Stack>
                          <Button
                            type="button"
                            onClick={handleLogout}
                            disabled={authLoading}
                            fullWidth
                          >
                            Cerrar sesión
                          </Button>
                        </Stack>
                      </Collapse>
                    </Box>
                  ) : (
                    <>
                      <Chip
                        label={`${session.claims?.email || 'Super Admin'}`}
                        color="primary"
                        variant="outlined"
                        sx={{
                          minWidth: 0,
                          maxWidth: { sm: 220, md: 120, lg: 220 },
                          '& .MuiChip-label': {
                            display: 'block',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                          },
                        }}
                      />
                      <Stack direction="row" spacing={0.75} alignItems="center" sx={{ flexShrink: 0 }}>
                        <Typography variant="body2" sx={{ whiteSpace: 'nowrap' }}>
                          Modo oscuro
                        </Typography>
                        <Switch
                          checked={colorMode === 'dark'}
                          onChange={handleToggleColorMode}
                          inputProps={{ 'aria-label': 'Alternar modo oscuro' }}
                        />
                      </Stack>
                      <Button type="button" onClick={handleLogout} disabled={authLoading} sx={{ flexShrink: 0, whiteSpace: 'nowrap' }}>
                        Cerrar sesión
                      </Button>
                    </>
                  )}
                </Box>
              </Toolbar>
            </AppBar>

            <LocationReviewActionsProvider
              key={session?.token || ''}
              pendingCount={isRegularUser ? locationPendingRequestCount : 0}
              sending={notificationSending}
              onSend={handleSendPendingRequestsEmail}
              onReview={() => setPendingOverviewOpen(true)}
              feedback={notificationFeedback}
            >
            {!activePage && (
              <Alert severity="info">No hay páginas asignadas para este nivel de acceso.</Alert>
            )}

            {activePage && (
              <activePage.Component
                accessToken={session?.token || ''}
                locationRequests={locationRequestState.requests}
                locationRequestsLoading={locationRequestState.loading}
                locationRequestsError={locationRequestState.error}
                locationRequestsLoaded={locationRequestState.hasLoaded}
                refreshLocationRequests={loadLocationRequests}
                onLocationRequestUpsert={upsertLocationRequest}
                onLocationRequestRemove={removeLocationRequest}
                onCancelRequest={isRegularUser ? openCancelRequest : undefined}
                cancellingRequests={cancellingRequests}
              />
            )}
            </LocationReviewActionsProvider>
          </Stack>
        )}
        {isRegularUser && (
          <Dialog open={pendingOverviewOpen} onClose={() => setPendingOverviewOpen(false)} maxWidth="lg" fullWidth>
            <DialogTitle>Cambios pendientes</DialogTitle>
            <DialogContent dividers>
              <PendingRequestsOverview requests={locationRequestState.requests} title=""
                onCancelRequest={openCancelRequest} cancelling={cancellingRequests} />
            </DialogContent>
            <DialogActions>
              <Button type="button" onClick={() => setPendingOverviewOpen(false)}>Cerrar</Button>
            </DialogActions>
          </Dialog>
        )}
        {isRegularUser && <CancelPendingRequestDialog
          open={Boolean(cancelSelection)} request={cancelSelection} cancelling={cancellingRequests}
          error={cancellationError} onClose={closeCancelRequest} onConfirm={confirmCancelRequest}
        />}
        <Portal><Snackbar open={Boolean(cancellationFeedback?.message)} autoHideDuration={7000}
          onClose={closeCancellationFeedback} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
          <Alert severity={cancellationFeedback?.type || 'success'} variant="filled"
            onClose={closeCancellationFeedback} sx={{ width: '100%' }}>
            {cancellationFeedback?.message}
          </Alert>
        </Snackbar></Portal>
      </Box>
    </ThemeProvider>
  );
}

export default App;
